import { DesignModels, Utils } from '@cadstrom/sigma-engine-shared';
import clsx from 'clsx';
import { memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { useEventCallback } from '#utils/use-event-callback.js';
import { CadRendererInstance } from './cad-renderer-instance.js';
import { CadRendererPlaceholder } from './cad-renderer-placeholder.js';
import { useActiveDesignSelectionContext } from './context/active-design-context.js';
import { useInteractionManagerContext } from './context/interaction-manager-context.js';
import { HierarchyNavigator } from './hierarchy-navigator.js';
import { HistoryNavigator } from './history-navigator.js';
import { moduleContainsPin } from './module-zoom-selection.js';
import { useSelectionHistoryController } from './selection-history-controller.js';
import {
	EMPTY_HISTORY,
	generateLabel,
	isAdditiveSelection,
	selectionsEqual,
	serializeSelection,
	useSelectionHistoryStore,
} from './selection-history-store.js';
import { SwitchableViews } from './switchable-views.js';
import { CadRendererSyncViewportChangeHandler } from './sync/bridges/view-hydration-bridge.js';
import { CadRendererSyncProvider } from './sync/cad-renderer-sync-context.js';
import { SyncStatusIndicator } from './sync/sync-status-indicator.js';

import type { InteractionState } from '@cadstrom/renderer';
import type { ModelWithNetStats, Module, PcbModel, SchematicModel } from '@cadstrom/sigma-engine-shared';
import type { ReactNode, Ref } from 'react';
import type { CadRendererSetActiveDesignEvent, ViewportState } from '#utils/events.js';
import type { CadRendererInstanceOwnProps, CadRendererInstanceRef } from './cad-renderer-instance.js';
import type { SelectionHistoryEntry } from './selection-history-store.js';
import type { CadRendererGridType } from './sync/bridges/grid-bridge.js';
import type { CadRendererSyncScope } from './sync/broadcast-manager.js';
import type { CadRendererActiveView } from './sync/sync-types.js';

export type ZoomToModuleOptions = {
	onlyOnFirstTime: boolean;
};

export type CadRendererRef = {
	zoomToModule: (moduleId: string, options: ZoomToModuleOptions) => void;
	deselectAll: () => void;
};

export type CadRendererProps = {
	revisionId: string;
	projectModel: ModelWithNetStats;
	ref?: Ref<CadRendererRef | null>;
	syncScope?: CadRendererSyncScope;

	className?: string;
	placeholderMessage?: string;
	imageUrlResolver?: (imageRef: string) => string | Promise<string>;
	onOpenInNewWindow?: (activeView: CadRendererActiveView) => void;

	/**
	 * Rendered in a pointer-transparent layer over the canvas area of every view
	 * (excluding the toolbar and status bar). Children position themselves with
	 * absolute placement.
	 */
	canvasOverlay?: ReactNode;

	/**
	 * Hides the selection-history navigator and the design selector, for scoped
	 * embeds that present a single fixed view. History is still recorded.
	 */
	hideNavigationControls?: boolean;

	/** Hides each view's tools column. */
	hideToolbar?: boolean;
	/** Hides each view's status bar. */
	hideStatusBar?: boolean;
	/** Grid style used for designs with no saved grid settings yet. Defaults to lines. */
	defaultGridType?: CadRendererGridType;
};

/** Must be rendered inside an `ActiveDesignContextProvider`, which owns the active-design state. */
export const CadRenderer = memo(function CadRenderer({
	revisionId,
	projectModel,
	ref,
	className,
	placeholderMessage,
	imageUrlResolver,
	onOpenInNewWindow,
	syncScope: syncScopeProp,
	canvasOverlay,
	hideNavigationControls = false,
	hideToolbar = false,
	hideStatusBar = false,
	defaultGridType,
}: CadRendererProps) {
	const { interactionManager: ctxInteractionManager, syncScope: interactionSyncScope } = useInteractionManagerContext();
	const syncScope = syncScopeProp ?? interactionSyncScope;

	const { coreModelWithDesignSummaries, designs } = useMemo(() => {
		const { designs, ...coreModel } = projectModel;
		return {
			coreModelWithDesignSummaries: {
				...coreModel,
				designSummaries: Object.fromEntries(
					Object.entries(designs).map(([designId, design]) => [
						designId,
						{
							modelType: design.modelType,
							designId: design.designId,
							modules: design.modules.map((mod) => ({
								ref: mod.ref,
								name: mod.name,
							})),
						},
					]),
				),
			},
			designs: Object.values(designs)
				.filter((d) => d.modelType === 'schematic')
				.toSorted((d1, d2) => {
					// Sort by sheetIndex ascending, then by designName
					const sheetIndexDiff = d1.sheetIndex - d2.sheetIndex;
					if (sheetIndexDiff !== 0) {
						return sheetIndexDiff;
					}
					return Utils.naturalStringSort(d1.designName ?? d1.designId, d2.designName ?? d2.designId);
				}),
		};
	}, [projectModel]);

	const { activeSelection, setActiveSelection } = useActiveDesignSelectionContext();
	// The page-level selection may reference a design this renderer does not show (e.g. a PCB
	// design, or one from a previously loaded model) — treat those as "nothing selected".
	const validSelection =
		activeSelection && designs.some((d) => d.designId === activeSelection.designId) ? activeSelection : null;
	const activeDesignId = validSelection?.designId ?? null;
	const activeInstanceId = validSelection?.instanceId;

	const instanceRefs = useRef<Record<string, CadRendererInstanceRef | null>>({});
	const zoomToModuleRef = useRef<string | undefined>(undefined);
	const pendingNavViewportRef = useRef<{ designId: string; instanceId?: string; viewport: ViewportState } | null>(null);
	const navEndTimerRef = useRef<number | null>(null);
	const designMountedRef = useRef(false);
	const activeSelectionRef = useRef<CadRendererActiveView>(validSelection);
	const syncHistorySuppressedUntilRef = useRef(0);

	const {
		push: historyPush,
		updateCurrentSelection,
		navigateTo: historyNavigateTo,
		setIsNavigating,
		isNavigating,
	} = useSelectionHistoryStore();

	const restorePendingViewportIfReady = useCallback(() => {
		const pending = pendingNavViewportRef.current;
		if (pending === null) {
			return;
		}

		const active = activeSelectionRef.current;
		if (active?.designId !== pending.designId || active.instanceId !== pending.instanceId) {
			return;
		}

		const instance = instanceRefs.current[pending.designId];
		if (!instance) {
			return;
		}

		instance.restoreViewport(pending.viewport);
		pendingNavViewportRef.current = null;
	}, []);

	useEffect(() => {
		activeSelectionRef.current = validSelection;
		restorePendingViewportIfReady();
	}, [validSelection, restorePendingViewportIfReady]);

	const suppressHistoryForHydration = useCallback(() => {
		syncHistorySuppressedUntilRef.current = Date.now() + 500;
	}, []);

	const getActiveViewport = useCallback(() => {
		if (activeSelectionRef.current?.designId === undefined) {
			return null;
		}
		return instanceRefs.current[activeSelectionRef.current.designId]?.getViewportState() ?? null;
	}, []);

	const restoreSyncViewport = useCallback(
		(designId: string, instanceId: string | undefined, viewport: ViewportState) => {
			pendingNavViewportRef.current = { designId, instanceId, viewport };
			restorePendingViewportIfReady();
		},
		[restorePendingViewportIfReady],
	);

	const handleOpenInNewWindow = useCallback(() => {
		onOpenInNewWindow?.(validSelection);
	}, [validSelection, onOpenInNewWindow]);

	/** Tracks the last designId + selection + viewport that was successfully pushed to history. */
	const lastRecordedRef = useRef<{
		designId: string | null | undefined;
		selection: ReturnType<typeof serializeSelection>;
		time: number;
		/** Initial viewport of the last pushed entry — used by the controller for pan-checkpoint diffing. */
		viewport: ViewportState | null;
	}>({ designId: undefined, selection: [], time: 0, viewport: null });

	/**
	 * Manages live viewport updates, resting-viewport capture, and pan-checkpoint creation.
	 * Callbacks close over reactive values directly; the hook re-reads them on every render
	 * via an internal ref, so timer callbacks always see the current manager/store state.
	 */
	const { onViewportChanged, onNavigationStarted, onNavigationEnded, onNewEntryPushed, cancelTimers } =
		useSelectionHistoryController({
			updateCurrentDelayedViewport: (vp) =>
				useSelectionHistoryStore.getState().updateCurrentDelayedViewport(revisionId, vp),
			updateCurrentRestingViewport: (vp) =>
				useSelectionHistoryStore.getState().updateCurrentRestingViewport(revisionId, vp),
			onPanCheckpointNeeded: (vp) => {
				const sel = serializeSelection(ctxInteractionManager.getCurrentSelection());
				const label = generateLabel(sel, activeDesignId, lastRecordedRef.current.designId, designs, {
					isPanCheckpoint: true,
				});
				historyPush(revisionId, {
					designId: activeDesignId,
					instanceId: activeInstanceId,
					selection: sel,
					viewport: vp,
					label,
				});
				lastRecordedRef.current = { designId: activeDesignId, selection: sel, time: Date.now(), viewport: vp };
			},
			isAtLatestEntry: () => {
				const { histories } = useSelectionHistoryStore.getState();
				const { currentIndex, entries } = histories[revisionId] ?? EMPTY_HISTORY;
				return currentIndex === entries.length - 1;
			},
			getLastPushedViewport: () => lastRecordedRef.current.viewport,
		});

	/** Set by the raw selectElementsByProperty event before React state settles. */
	const pendingAdditiveRef = useRef(false);

	/** Debounce timer for batching rapid selection + design-change events. */
	const recordDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	/**
	 * Executes when the debounce timer fires.
	 *
	 * @param scheduledForProject - The project model reference captured when the debounce was
	 *   scheduled, used to discard callbacks that survived a project switch.
	 */
	const doHistoryRecord = useEventCallback((scheduledForProject: ModelWithNetStats) => {
		if (Date.now() < syncHistorySuppressedUntilRef.current) {
			return;
		}

		if (isNavigating || projectModel !== scheduledForProject) {
			return;
		}

		const sel = serializeSelection(ctxInteractionManager.getCurrentSelection());
		const last = lastRecordedRef.current;

		if (activeDesignId === last.designId && selectionsEqual(sel, last.selection)) {
			return;
		}

		const { histories } = useSelectionHistoryStore.getState();
		const { currentIndex, entries } = histories[revisionId] ?? EMPTY_HISTORY;
		const isAdditive =
			pendingAdditiveRef.current &&
			activeDesignId === last.designId &&
			isAdditiveSelection(last.selection, sel) &&
			currentIndex === entries.length - 1;
		pendingAdditiveRef.current = false;

		if (isAdditive) {
			const label = generateLabel(sel, activeDesignId, last.designId, designs);
			updateCurrentSelection(revisionId, sel, label);
			lastRecordedRef.current = {
				...lastRecordedRef.current,
				designId: activeDesignId,
				selection: sel,
				time: Date.now(),
			};
		} else {
			const viewport = activeDesignId ? (instanceRefs.current[activeDesignId]?.getViewportState() ?? null) : null;
			const label = generateLabel(sel, activeDesignId, last.designId, designs);
			historyPush(revisionId, {
				designId: activeDesignId,
				instanceId: activeInstanceId,
				selection: sel,
				viewport,
				label,
			});
			onNewEntryPushed();
			lastRecordedRef.current = { designId: activeDesignId, selection: sel, time: Date.now(), viewport };
		}
	});

	/** Debounces rapid selection and design-change events, then snapshots and pushes history. */
	const scheduleHistoryRecord = useEventCallback(() => {
		if (Date.now() < syncHistorySuppressedUntilRef.current) {
			return;
		}

		if (recordDebounceRef.current !== null) {
			clearTimeout(recordDebounceRef.current);
		}
		const scheduledForProject = projectModel;
		recordDebounceRef.current = setTimeout(() => {
			recordDebounceRef.current = null;
			doHistoryRecord(scheduledForProject);
		}, 100);
	});

	// Subscribe to raw selection events to capture the addToSelection flag
	useEffect(() => {
		const handleSelect = ({ addToSelection }: { addToSelection: boolean }) => {
			pendingAdditiveRef.current = addToSelection;
			scheduleHistoryRecord();
		};
		const handleClear = () => {
			pendingAdditiveRef.current = false;
			scheduleHistoryRecord();
		};
		ctxInteractionManager.on('selectElementsByProperty', handleSelect);
		ctxInteractionManager.on('clearSelection', handleClear);
		return () => {
			ctxInteractionManager.removeListener('selectElementsByProperty', handleSelect);
			ctxInteractionManager.removeListener('clearSelection', handleClear);
		};
	}, [ctxInteractionManager, scheduleHistoryRecord]);

	// Record a history entry whenever the active design/sheet changes.
	// Skips the initial mount to avoid a spurious entry before the user navigates.
	useEffect(() => {
		if (!designMountedRef.current) {
			designMountedRef.current = true;
			return;
		}
		pendingAdditiveRef.current = false; // design changes are never additive
		scheduleHistoryRecord();
	}, [activeDesignId, activeInstanceId, scheduleHistoryRecord]);

	// Cancel any pending debounce and restore lastRecordedRef when the project changes.
	// Restoring lastRecordedRef from the project's last entry prevents duplicate entries when
	// switching back to a previously visited project.
	useEffect(() => {
		if (recordDebounceRef.current !== null) {
			clearTimeout(recordDebounceRef.current);
			recordDebounceRef.current = null;
		}
		cancelTimers();
		const lastEntry = useSelectionHistoryStore.getState().histories[revisionId]?.entries.at(-1);
		if (lastEntry) {
			lastRecordedRef.current = {
				designId: lastEntry.designId,
				selection: lastEntry.selection,
				time: lastEntry.timestamp,
				viewport: lastEntry.viewport,
			};
		} else {
			lastRecordedRef.current = { designId: undefined, selection: [], time: 0, viewport: null };
		}
	}, [revisionId, cancelTimers]);

	/** Restore the full application state (design + selection + viewport) for a history entry. */
	const navigateToHistoryEntry = useCallback(
		(entry: SelectionHistoryEntry, targetIndex: number) => {
			pendingNavViewportRef.current = null;
			window.clearTimeout(navEndTimerRef.current ?? undefined);
			setIsNavigating(true);
			onNavigationStarted();
			historyNavigateTo(revisionId, targetIndex);

			// Restore the active design
			if (entry.designId !== null) {
				setActiveSelection({ designId: entry.designId, instanceId: entry.instanceId });
			} else {
				setActiveSelection(null);
			}

			// Restore the selection through InteractionState so history replay emits the selection
			// event even when the serialized selection is equal on a different design.
			replayHistorySelection(ctxInteractionManager, entry);

			// Prefer a settled viewport over the initial snapshot captured at push time.
			const viewportToRestore = entry.restingViewport ?? entry.delayedViewport ?? entry.viewport;
			if (viewportToRestore !== null && entry.designId !== null) {
				restoreSyncViewport(entry.designId, entry.instanceId, viewportToRestore);
			}

			// Update the last-recorded ref so the debounced recorder sees no change
			lastRecordedRef.current = {
				designId: entry.designId,
				selection: entry.selection,
				time: Date.now(),
				viewport: viewportToRestore,
			};

			// Re-enable history recording after state has settled
			navEndTimerRef.current = window.setTimeout(() => {
				setIsNavigating(false);
				onNavigationEnded();
			}, 200);
		},
		[
			ctxInteractionManager,
			historyNavigateTo,
			onNavigationEnded,
			onNavigationStarted,
			restoreSyncViewport,
			revisionId,
			setActiveSelection,
			setIsNavigating,
		],
	);

	// Cancel all pending timers on unmount to prevent post-unmount state mutations.
	// Also reset the navigating flag in case unmount races with an in-flight navigation.
	useEffect(() => {
		return () => {
			window.clearTimeout(navEndTimerRef.current ?? undefined);
			window.clearTimeout(recordDebounceRef.current ?? undefined);
			setIsNavigating(false);
			onNavigationEnded();
		};
	}, [setIsNavigating, onNavigationEnded]);

	const handleZoomToModule = useCallback((designId: string, moduleRef: string) => {
		const rendererInstanceRef = instanceRefs.current[designId];
		rendererInstanceRef?.zoomToModule(moduleRef);
	}, []);

	useEffect(() => {
		function checkPinMatch(event: CadRendererSetActiveDesignEvent, d: SchematicModel | PcbModel, m: Module) {
			// No pin specified, so we're a match
			if (
				event.detail.symbolPin === undefined &&
				event.detail.footprintPad === undefined &&
				event.detail.signalId === undefined
			) {
				return true;
			}

			if (event.detail.symbolPin !== undefined) {
				if (d.modelType !== 'schematic') {
					return false;
				}

				return (
					m.pinNets[event.detail.symbolPin] !== undefined ||
					moduleContainsPin(m, coreModelWithDesignSummaries.components, event.detail.symbolPin, 'symbolPin')
				);
			}

			if (event.detail.footprintPad !== undefined) {
				if (d.modelType !== 'pcb') {
					return false;
				}

				return (
					m.pinNets[event.detail.footprintPad] !== undefined ||
					moduleContainsPin(m, coreModelWithDesignSummaries.components, event.detail.footprintPad, 'footprintPad')
				);
			}

			if (event.detail.signalId !== undefined) {
				const findSignal = coreModelWithDesignSummaries.components[m.componentRef]?.signals.find(
					(s) => s.id === event.detail.signalId,
				);

				if (d.modelType === 'schematic') {
					return findSignal?.pinRefs.some(
						(pinRef) =>
							pinRef !== null &&
							(m.pinNets[pinRef] !== undefined ||
								moduleContainsPin(m, coreModelWithDesignSummaries.components, pinRef, 'symbolPin')),
					);
				} else {
					return findSignal?.padRefs.some(
						(padRef) =>
							m.pinNets[padRef] !== undefined ||
							moduleContainsPin(m, coreModelWithDesignSummaries.components, padRef, 'footprintPad'),
					);
				}
			}

			return false;
		}

		const handleCadRendererSetActiveDesign = (event: CadRendererSetActiveDesignEvent) => {
			let design: (SchematicModel | PcbModel) | undefined = undefined;
			let activeInstanceId: string | undefined = event.detail.instanceId;

			if (event.detail.moduleReferenceDesignator !== undefined) {
				// Find design by module
				design =
					// First find by exact match
					designs.find((d) =>
						d.modules.some((m) => m.name === event.detail.moduleReferenceDesignator && checkPinMatch(event, d, m)),
					) ??
					designs.find((d) =>
						d.modules.some(
							(m) => m.fullModuleName === event.detail.moduleReferenceDesignator && checkPinMatch(event, d, m),
						),
					);

				// Ensure valid instanceId
				if (
					activeInstanceId &&
					!design?.multiInstanceConfiguration?.instances.find((i) => i.instanceId === activeInstanceId)
				) {
					activeInstanceId = undefined;
				}

				const module = design?.modules.find((m) => m.name === event.detail.moduleReferenceDesignator);
				if (module) {
					activeInstanceId ??= module.multiInstance?.instanceId;
				}
			} else if (event.detail.net !== undefined) {
				// Find design by net
				design = designs.find((d) => d.nets[event.detail.net!]);
				// Ensure valid instanceId
				if (
					activeInstanceId &&
					!design?.multiInstanceConfiguration?.instances.find((i) => i.instanceId === activeInstanceId)
				) {
					activeInstanceId = undefined;
				}

				if (design) {
					// Find first instance that contains the net
					activeInstanceId ??= design.modules.find((m) => m.pinNets[event.detail.net!] !== undefined)?.multiInstance
						?.instanceId;
				}
			} else if (event.detail.designId !== undefined) {
				// Find design by designId
				design = designs.find((d) => d.designId === event.detail.designId);

				// Ensure valid instanceId
				if (
					activeInstanceId &&
					!design?.multiInstanceConfiguration?.instances.find((i) => i.instanceId === activeInstanceId)
				) {
					activeInstanceId = undefined;
				}
			}

			if (design) {
				setActiveSelection({ designId: design.designId, instanceId: activeInstanceId });
			}
		};

		addEventListener('cadRendererSetActiveDesign', handleCadRendererSetActiveDesign);
		return () => removeEventListener('cadRendererSetActiveDesign', handleCadRendererSetActiveDesign);
	});

	useImperativeHandle(ref, () => ({
		zoomToModule: (moduleRef, { onlyOnFirstTime }) => {
			if (onlyOnFirstTime && activeDesignId !== null) {
				return;
			}

			// Find the design that contains the module
			const design =
				designs.find((d) => d.modules.some((m) => m.name === moduleRef)) ??
				designs.find((d) => d.modules.some((m) => m.fullModuleName === moduleRef));
			if (!design) {
				return;
			}

			const module = design.modules.find((m) => m.name === moduleRef || m.fullModuleName === moduleRef);
			if (!module) {
				return;
			}

			setActiveSelection({ designId: design.designId, instanceId: module.multiInstance?.instanceId });
			zoomToModuleRef.current = moduleRef;

			const instance = instanceRefs.current[design.designId];
			if (instance) {
				handleZoomToModule(design.designId, moduleRef);
			}
		},
		deselectAll: () => {
			ctxInteractionManager.clearSelection();
		},
	}));

	const getViewId = (designId: string, instanceId?: string) => `${designId}~${instanceId}`;

	const viewProps = useMemo(
		() =>
			Object.fromEntries(
				designs.map((design) => [
					getViewId(design.designId, activeInstanceId),
					{
						design,
						canvasOverlay,
						hideToolbar,
						hideStatusBar,
						onOpenInNewWindow: onOpenInNewWindow ? handleOpenInNewWindow : undefined,
						ref: (instance: CadRendererInstanceRef | null) => {
							instanceRefs.current[design.designId] = instance;
							if (instance === null) {
								return;
							}

							if (zoomToModuleRef.current !== undefined) {
								if (
									design.modules.some(
										(m) => m.name === zoomToModuleRef.current || m.fullModuleName === zoomToModuleRef.current,
									)
								) {
									handleZoomToModule(design.designId, zoomToModuleRef.current);
									zoomToModuleRef.current = undefined;
								}
							}

							restorePendingViewportIfReady();
						},
						cadRendererCanvasProps: {
							revisionId,
							coreModelWithDesignSummaries,
							design,
							multiInstanceId: activeInstanceId,
							imageUrlResolver,
						},
					} satisfies CadRendererInstanceOwnProps,
				]),
			),
		[
			designs,
			activeInstanceId,
			revisionId,
			coreModelWithDesignSummaries,
			imageUrlResolver,
			canvasOverlay,
			hideToolbar,
			hideStatusBar,
			handleZoomToModule,
			handleOpenInNewWindow,
			onOpenInNewWindow,
			restorePendingViewportIfReady,
		],
	);

	const hierarchy = useMemo(() => {
		return projectModel.designHierarchy ?? DesignModels.hierarchyFromDesigns(projectModel.designs);
	}, [projectModel.designHierarchy, projectModel.designs]);

	const el = useDataElTagGenerator(CadRenderer);
	return (
		<CadRendererSyncProvider
			syncScope={syncScope}
			interactionManager={ctxInteractionManager}
			activeView={validSelection}
			setActiveView={setActiveSelection}
			getActiveViewport={getActiveViewport}
			restoreViewport={restoreSyncViewport}
			suppressHistoryForHydration={suppressHistoryForHydration}
			defaultGridType={defaultGridType}
		>
			<CadRendererSyncViewportChangeHandler revisionId={revisionId} onViewportChanged={onViewportChanged} />
			<div {...el()} className={clsx(className, 'relative grid place-items-stretch')}>
				<div {...el('content')} className='absolute inset-0 flex flex-col'>
					{(syncScope !== undefined || !hideNavigationControls) && (
						<div
							{...el('header')}
							className='absolute top-0 right-0 z-10 flex w-[calc(100%-2rem)] justify-end gap-3 p-1.5'
						>
							{syncScope && (
								<div className='flex items-center rounded-lg'>
									<SyncStatusIndicator />
								</div>
							)}
							{!hideNavigationControls && (
								<>
									<HistoryNavigator revisionId={revisionId} onNavigateTo={navigateToHistoryEntry} />
									<HierarchyNavigator
										hierarchy={hierarchy}
										selectedDesignId={activeDesignId}
										selectedInstanceId={activeInstanceId}
										onSelect={setActiveSelection}
										className='w-[min(24rem,80%)] bg-gray-2/60 backdrop-blur-md'
									/>
								</>
							)}
						</div>
					)}

					<div className='isolate flex min-h-0 grow basis-0'>
						{activeDesignId === null ?
							<CadRendererPlaceholder message={placeholderMessage} />
						:	<SwitchableViews
								ViewComponent={CadRendererInstance}
								viewProps={viewProps}
								selectedViewId={getViewId(activeDesignId, activeInstanceId)}
								maxBackgroundViews={8}
								backgroundTimeoutMs={10 * 60 * 1000} // 10 minutes
							/>
						}
					</div>
				</div>
			</div>
		</CadRendererSyncProvider>
	);
});

function replayHistorySelection(interactionManager: InteractionState, entry: SelectionHistoryEntry): void {
	let replayedCount = 0;
	for (const [property, values] of entry.selection) {
		for (const value of values) {
			interactionManager.selectElementsByProperty(property, value, replayedCount > 0);
			replayedCount += 1;
		}
	}

	if (replayedCount === 0) {
		interactionManager.clearSelection();
	}
}
